import React, { useEffect, useMemo, useRef, useState } from "react";
import Error from "../../../../Alerts/Error";
import { fileTypeFromBlob } from 'file-type';
import Spinner from "../../../../Spinner";
import { queuePositionLabel } from "../../../../../util/uploadQueue";
import CancelUpload, { useUploadCancel } from "./CancelUpload";
import { claimSelection, releaseClaim, uploadOwner, DUPLICATE_IN_ENTRY_MESSAGE } from "../../../../../util/duplicateFiles";
import { EMFJS, RTFJS, WMFJS } from 'rtf.js';

import filterResponse from "../../../../../filterResponse";
import PostalMime from "postal-mime";
import { extname } from "../../../../../util/path";
import { stringToArrayBuffer } from "../../../../../util/functions";
const allowedPreviewExtensions = ['pdf', 'txt', 'json', 'csv', 'tsv', 'eml', 'html'];

export default function Text({ formId, uploadFile, fileName, setFile, fileKey, file }) {
    const fileRef = useRef();
    const docRef = useRef();
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(false);
    const upload = useUploadCancel();

    // These inputs show a plain spinner rather than a progress bar, which says nothing about a
    // file that has not started yet. On an entry with dozens of inputs only a couple upload at a
    // time, so a small file picked last can wait a while behind the big ones.
    const [queuePosition, setQueuePosition] = useState(null);
    const reportQueuePosition = (loaded, total, phase, position) => setQueuePosition(
        prev => {
            const next = phase === "queued" ? position : null;
            return prev === next ? prev : next; // React bails out, so the upload's own ticks don't re-render this
        },
    );

    async function onChange() {
        // Held here rather than read off the element again after each await: one upload runs at a
        // time and the input is disabled while it does, but the checks below happen before that.
        const chosen = fileRef.current.files[0];
        if (!chosen) {
            return;
        }

        // Turned away the moment it is picked rather than when the queue gets round to hashing it:
        // a file waits minutes for its turn, and the second copy is picked during that wait, while
        // the first is still being checked — which is how this was reported (DEV-3191).
        const owner = uploadOwner(formId, file.id);
        if (!claimSelection(chosen, owner)) {
            fileRef.current.value = null;
            return setErrors([DUPLICATE_IN_ENTRY_MESSAGE]);
        }

        // A file the checks below turn away is not this input's after all, so it must stop being
        // held against the others. One that reaches the upload keeps its claim, which the upload
        // itself releases if it fails.
        let reachedUpload = false;

        try {
            // if there is already a file, don't hide the iframe as it will be removed if upload fails
            if (!fileKey) {
                docRef.current.removeAttribute('srcdoc');
                docRef.current.classList.add("d-none");
            }

            setLoading(true);
            setErrors([]);
            const signal = upload.begin();

            let { size, name } = chosen;
            size /= 1000000;

            if (size > Number(file.properties.maxFileSize)) {
                fileRef.current.value = null;
                return setErrors([`File exceeded maximum file size of ${file.properties.maxFileSize} MB`]);
            }
            else if (size < Number(file.properties.minFileSize)) {
                fileRef.current.value = null;
                return setErrors([`File does not have the minimum file size of ${file.properties.minFileSize} MB`]);
            }
            else {
                const fileType = await fileTypeFromBlob(chosen);

                const mimeTypes = [
                    'application/vnd.oasis.opendocument.text',
                    'application/vnd.oasis.opendocument.spreadsheet',
                    'application/vnd.oasis.opendocument.presentation',
                    'application/msword',
                    'application/x-cfb',
                    'application/vnd.ms-excel',
                    'application/vnd.ms-powerpoint',
                    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'application/pdf',
                    'application/rtf'
                ];

                if (fileType !== undefined && !mimeTypes.includes(fileType.mime)) {
                    fileRef.current.value = null;
                    return setErrors(["This is not a text file."]);
                }
                // check for the correct file extension
                if (fileType && !file.properties.extensions.includes('.' + fileType.ext)) {
                    fileRef.current.value = null;
                    return setErrors(["The extension of your file is not allowed, the allowed extensions are: " + file.properties.extensions.toString()]);
                }
                else if (!fileType) {
                    const fileExtension = extname(name);
                    if (!file.properties.extensions.includes(fileExtension)) {
                        fileRef.current.value = null;
                        return setErrors(["The extension of your file is not allowed, the allowed extensions are: " + file.properties.extensions.toString()]);
                    }
                }
                else if (!file.properties.extensions.includes(extname(name))) {
                    if (!extname(name)) {
                        name += '.' + fileType.ext;
                    }
                    else {
                        name = name.split('.').slice(0, -1).join('.') + '.' + fileType.ext
                    }
                }

                let toUpload = chosen;

                if (chosen.name !== name) {
                    const blob = new Blob([chosen], { type: chosen.type });
                    toUpload = new File([blob], name, { type: chosen.type });
                }

                setQueuePosition(null); // a place in line left over from the previous pick is not this one's
                reachedUpload = true;
                const key = await uploadFile(toUpload, undefined, reportQueuePosition, signal);

                if (key) {
                    setFile(key, name);
                } else if (fileKey) {
                    // if there is already a file key, and new file is couldn't be uploaded, keep the old file key
                    fileRef.current.value = null;
                } else {
                    fileRef.current.value = null;
                    setFile('', '');
                }
            }
        }
        catch (e) {
            // Anything unexpected still has to say so. Clearing the input with no message leaves the
            // contributor looking at an empty field, assuming they did something wrong.
            console.error(e);
            fileRef.current.value = null;
            setErrors(["Something went wrong while adding your file. Please try again, and let us know if it keeps happening."]);
        }
        finally {
            if (!reachedUpload) {
                releaseClaim(owner);
            }
            upload.end();
            setLoading(false);
        }
    }

    useEffect(() => {
        let isMounted = true;
        const abortController = new AbortController();

        (async () => {
            setLoading(true);

            try {
                if (fileKey) {
                    if (fileKey.endsWith('.eml')) {
                        const response = await fetch(process.env.REACT_APP_ENDPOINT + '/files/' + fileKey, {
                            credentials: "include",
                            abortController: abortController.signal
                        });
                        await filterResponse(response);
                        const blob = await response.blob();
                        const mail = await PostalMime.parse(blob);
                        if (isMounted) {
                            if (mail.html) {
                                docRef.current.srcdoc = mail.html;
                            }
                            else if (mail.text) {
                                const escaped = mail.text
                                    .replace(/&/g, "&amp;")
                                    .replace(/</g, "&lt;")
                                    .replace(/>/g, "&gt;");
                                docRef.current.srcdoc = `<pre style="white-space: pre-wrap; font-family: sans-serif;">${escaped}</pre>`;
                            }
                            docRef.current.classList.remove("d-none");
                        }
                    }
                    else if (fileKey.endsWith('.rtf')) {
                        const response = await fetch(process.env.REACT_APP_ENDPOINT + '/files/' + fileKey, {
                            credentials: "include",
                            abortController: abortController.signal
                        });
                        await filterResponse(response);
                        const blob = await response.text();
                        const rtfDoc = new RTFJS.Document(stringToArrayBuffer(blob));
                        const html = await rtfDoc.render();

                        const doc = document.createElement("html");
                        const body = document.createElement("body");
                        for (const element of html) {
                            body.append(element);
                        }
                        doc.append(body);

                        isMounted && (docRef.current.srcdoc = doc.outerHTML);
                        isMounted && docRef.current.classList.remove("d-none");
                    }
                    else if (allowedPreviewExtensions.includes(fileKey.split('.').pop())) {
                        isMounted && (docRef.current.src = process.env.REACT_APP_ENDPOINT + '/files/' + fileKey);
                        isMounted && docRef.current.classList.remove("d-none");
                    }
                    else {
                        isMounted && docRef.current.classList.add("d-none");
                    }
                    const myFile = new File(['preview'], fileName);
                    const dataTransfer = new DataTransfer();
                    dataTransfer.items.add(myFile);
                    isMounted && (fileRef.current.files = dataTransfer.files);
                }
                else {
                    isMounted && docRef.current.classList.add("d-none");
                    isMounted && (fileRef.current.value = null);
                }
            }
            catch (e) {
                console.error(e);
                isMounted && docRef.current.classList.add("d-none");
            }

            isMounted && setLoading(false);
        })();

        return () => {
            isMounted = false;
            abortController.abort();
        };
    }, [fileKey]);

    // The preview is served inline, so without this there is no way to save the file back out —
    // and the formats the iframe cannot render (docx, xlsx) show nothing at all on their own.
    const downloadUrl = useMemo(() => {
        const urlSearchParams = new URLSearchParams();
        if (fileName) {
            urlSearchParams.append('originalFileName', fileName);
        }
        urlSearchParams.append('redirect', '1'); // download straight from Spaces
        urlSearchParams.append('download', '1'); // force attachment with the original filename
        return process.env.REACT_APP_ENDPOINT + '/files/' + fileKey + '?' + urlSearchParams.toString();
    }, [fileKey, fileName]);

    const sandbox = useMemo(() => {
        if (fileKey.endsWith('.html')) {
            return "allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-forms"
        }
        return null;
    }, [fileKey]);

    return (
        <div className={"overflow-x-auto"}>
            <label htmlFor={formId + file.id} className={"form-label"}>{file.name}:</label>
            <input disabled={loading} id={formId + file.id} form={formId} required={!fileKey} ref={fileRef} onChange={onChange} type={"file"} className={"form-control"} name={file.name}
                accept={file.properties.extensions.toString()} />
            {loading &&
                <div className="mt-3">
                    <Spinner />
                    {queuePosition > 0 && <small className="text-muted ms-2">{queuePositionLabel(queuePosition)}</small>}
                    {upload.active && <div><CancelUpload onCancel={upload.cancel} /></div>}
                </div>
            }
            <iframe {...(sandbox && { sandbox: sandbox })} width={'100%'} height={250} ref={docRef} ></iframe>
            {fileKey && <a target={"_blank"} rel={"noreferrer"} className={"btn btn-sm btn-secondary mt-2"} href={downloadUrl}>Download</a>}

            {/* Last, so a refusal reads as this input's and not the next one's. */}
            {errors.map((value, index) => {
                return <Error key={index} message={value} />;
            })}
        </div>
    );
}
